<template>
    <div class="notif-page">
        <!-- Compact Header Bar -->
        <div class="notif-header-bar">
            <div class="notif-header-left">
                <div class="notif-icon-wrap">
                    <i class="fas fa-bell"></i>
                </div>
                <h2 class="notif-title">Notifications</h2>
                <span class="notif-badge" v-if="unreadCount > 0">{{ unreadCount }}</span>
            </div>
            <div class="notif-header-right">
                <div class="notif-search-wrap">
                    <i class="fas fa-search notif-search-icon"></i>
                    <input
                        type="text"
                        class="notif-search-input"
                        placeholder="Search..."
                        v-model="searchText"
                        @keyup.enter="fetchNotifications(searchText)"
                    />
                    <button v-if="searchText" class="notif-search-clear" @click="searchText = ''; fetchNotifications('')">
                        <i class="fas fa-times"></i>
                    </button>
                </div>
                <input v-model="startDate" class="notif-date-input" type="date" title="From date" />
                <input v-model="endDate" class="notif-date-input" type="date" title="To date" />
                <button class="notif-filter-btn" @click="fetchNotifications(searchText)" :disabled="loading">
                    <i class="fas fa-filter" v-if="!loading"></i>
                    <div class="notif-spin" v-else></div>
                    Filter
                </button>
            </div>
        </div>

        <!-- Content -->
        <div class="notif-body">
            <!-- Loading -->
            <div v-if="loading && !notifications.length" class="notif-loading">
                <div class="notif-spinner"></div>
                <span>Loading...</span>
            </div>

            <!-- Empty -->
            <div v-else-if="!loading && !notifications.length" class="notif-empty">
                <div class="notif-empty-icon"><i class="fas fa-bell-slash"></i></div>
                <h3>No notifications</h3>
                <p>Adjust your filters or check back later</p>
            </div>

            <!-- List -->
            <div v-else class="notif-list">
                <div
                    class="notif-card"
                    v-for="(notification, idx) in notifications"
                    :key="notification.id"
                    :class="{ 'notif-card--unread': !notification.read }"
                    :style="{ animationDelay: `${idx * 0.015}s` }"
                >
                    <div class="notif-card-left">
                        <div class="notif-card-icon" :class="getNotifColor(notification.title)">
                            <i :class="getNotifIcon(notification.title)"></i>
                        </div>
                    </div>
                    <div class="notif-card-body">
                        <div class="notif-card-text">{{ formatNotifText(notification.title) }}</div>
                        <div class="notif-card-meta">
                            <span class="notif-card-time"><i class="fas fa-clock"></i> {{ formatIndianDate(notification.createdAt) }}</span>
                            <span class="notif-card-unread-dot" v-if="!notification.read"></span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Pagination Footer -->
            <div class="notif-pagination-bar" v-if="notifications.length">
                <div class="notif-page-info">
                    <span class="notif-page-info-text">
                        Showing <strong>{{ rangeStart }}–{{ rangeEnd }}</strong> of <strong>{{ totalRecords }}</strong>
                    </span>
                    <div class="notif-per-page">
                        <label>Per page</label>
                        <select v-model.number="limit" @change="onLimitChange" class="notif-per-page-select">
                            <option :value="15">15</option>
                            <option :value="25">25</option>
                            <option :value="50">50</option>
                            <option :value="100">100</option>
                        </select>
                    </div>
                </div>
                <div class="notif-page-nav" v-if="totalPages > 1">
                    <button class="notif-pg-btn" :disabled="page <= 1" @click="goPage(1)" title="First">
                        <i class="fas fa-angle-double-left"></i>
                    </button>
                    <button class="notif-pg-btn" :disabled="page <= 1" @click="goPage(page - 1)" title="Previous">
                        <i class="fas fa-angle-left"></i>
                    </button>
                    <template v-for="(p, pIdx) in visiblePages">
                        <span v-if="p === '...'" :key="'ellipsis-'+pIdx" class="notif-pg-ellipsis">...</span>
                        <button v-else :key="'pg-'+p" class="notif-pg-btn" :class="{ 'notif-pg-btn--active': p === page }" @click="goPage(p)">
                            {{ p }}
                        </button>
                    </template>
                    <button class="notif-pg-btn" :disabled="page >= totalPages" @click="goPage(page + 1)" title="Next">
                        <i class="fas fa-angle-right"></i>
                    </button>
                    <button class="notif-pg-btn" :disabled="page >= totalPages" @click="goPage(totalPages)" title="Last">
                        <i class="fas fa-angle-double-right"></i>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { formatIndianDate, daysToMs } from "../../utils/common"
import { http } from "@/utils/http"

export default {
    data() {
        return {
            startDate: "",
            endDate: "",
            searchText: "",
            loading: true,
            notifications: [],
            totalPages: 0,
            totalRecords: 0,
            page: 1,
            limit: 15,
            initialized: false,
        }
    },
    computed: {
        unreadCount() {
            return this.notifications ? this.notifications.filter((item) => !item.read).length : 0
        },
        rangeStart() {
            return this.totalRecords === 0 ? 0 : (this.page - 1) * this.limit + 1
        },
        rangeEnd() {
            return Math.min(this.page * this.limit, this.totalRecords)
        },
        visiblePages() {
            const total = this.totalPages
            const current = this.page
            if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
            const pages = []
            pages.push(1)
            if (current > 3) pages.push("...")
            const start = Math.max(2, current - 1)
            const end = Math.min(total - 1, current + 1)
            for (let i = start; i <= end; i++) pages.push(i)
            if (current < total - 2) pages.push("...")
            pages.push(total)
            return pages
        },
    },
    async mounted() {
        this.loading = false
        this.startDate = new Date(Date.now() - daysToMs(7)).toISOString().slice(0, 10)
        this.endDate = new Date().toISOString().slice(0, 10)
        await this.fetchNotifications()
    },
    methods: {
        formatIndianDate,
        formatNotifText(text) {
            if (!text) return text
            return text.replace(/(\d+\.\d{3,})/g, (match) => parseFloat(match).toFixed(2))
        },
        getNotifIcon(title) {
            if (!title) return "fas fa-bell"
            const t = title.toLowerCase()
            if (t.includes("checked in")) return "fas fa-sign-in-alt"
            if (t.includes("checked out")) return "fas fa-sign-out-alt"
            if (t.includes("overtime")) return "fas fa-clock"
            if (t.includes("leave")) return "fas fa-calendar-minus"
            if (t.includes("late")) return "fas fa-exclamation-circle"
            if (t.includes("request")) return "fas fa-paper-plane"
            return "fas fa-bell"
        },
        getNotifColor(title) {
            if (!title) return 'notif-clr-default'
            const t = title.toLowerCase()
            if (t.includes("checked in")) return "notif-clr-success"
            if (t.includes("checked out")) return "notif-clr-info"
            if (t.includes("overtime")) return "notif-clr-warning"
            if (t.includes("leave")) return "notif-clr-purple"
            if (t.includes("late")) return "notif-clr-danger"
            if (t.includes("request")) return "notif-clr-info"
            return "notif-clr-default"
        },
        async markRead() {
            if (!this.notifications || !this.notifications.length) return
            try {
                const unreadIds = this.notifications
                    .filter((item) => !item.read)
                    .map((item) => item.id)
                if (!unreadIds.length) return
                const res = await http.post(`/notifications/mark-read?ids=${unreadIds}`)
                if (res.data && res.data.result) {
                    this.notifications = res.data.result
                }
            } catch (err) {
                console.log("error in fetching notifications", err)
            }
        },
        async fetchNotifications(searchStr = "") {
            try {
                this.loading = true
                const res = await http.get(
                    `/notifications?page=${this.page}&limit=${this.limit}&start=${this.startDate}&end=${this.endDate}&search=${searchStr}`
                )
                this.totalRecords = res.data.total || 0
                this.totalPages = Math.ceil(this.totalRecords / this.limit)
                this.notifications = res.data.result
                this.markRead()
            } catch (err) {
                console.log("error in fetching notifications", err)
            }
            this.loading = false
            this.initialized = true
        },
        goPage(p) {
            if (p < 1 || p > this.totalPages || p === this.page || !this.initialized) return
            this.page = p
            window.scrollTo({ top: 0, behavior: 'smooth' })
            this.fetchNotifications(this.searchText)
        },
        onLimitChange() {
            this.page = 1
            this.fetchNotifications(this.searchText)
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.notif-page {
    animation: notifFadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
@keyframes notifFadeIn {
    from { opacity: 0; transform: translateY(4px); }
    to { opacity: 1; transform: translateY(0); }
}

/* ========== COMPACT HEADER BAR ========== */
.notif-header-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    background: white;
    border: 1px solid $gray100;
    border-radius: $radiusMd;
    padding: 10px 16px;
    margin-bottom: 10px;
    box-shadow: $shadowSm;
    flex-wrap: wrap;
}
.notif-header-left {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
}
.notif-icon-wrap {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: linear-gradient(135deg, $primaryLight, darken($primaryLight, 4%));
    color: $primaryColor;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    flex-shrink: 0;
}
.notif-title {
    font-size: 15px;
    font-weight: 700;
    color: $gray800;
    margin: 0;
    letter-spacing: -0.01em;
    white-space: nowrap;
}
.notif-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: $radiusFull;
    font-size: 11px;
    font-weight: 700;
    background: $danger;
    color: white;
    line-height: 1;
}
.notif-header-right {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}
.notif-search-wrap {
    position: relative;
    min-width: 160px;
    max-width: 220px;
}
.notif-search-icon {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: $gray400;
    font-size: 12px;
    pointer-events: none;
}
.notif-search-input {
    width: 100%;
    padding: 7px 28px 7px 30px;
    border: 1px solid $gray200;
    border-radius: 6px;
    font-size: 12.5px;
    color: $gray700;
    background: $gray50;
    outline: none;
    transition: all $transitionFast;
    font-family: inherit;

    &::placeholder { color: $gray400; }
    &:focus {
        border-color: $primaryColor;
        box-shadow: 0 0 0 2px rgba(0, 94, 137, 0.08);
        background: white;
    }
}
.notif-search-clear {
    position: absolute;
    right: 6px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: $gray400;
    cursor: pointer;
    padding: 2px;
    font-size: 11px;

    &:hover { color: $gray600; }
}
.notif-date-input {
    padding: 7px 8px;
    border: 1px solid $gray200;
    border-radius: 6px;
    font-size: 12.5px;
    color: $gray700;
    background: $gray50;
    outline: none;
    font-family: inherit;
    transition: all $transitionFast;
    max-width: 140px;

    &:focus {
        border-color: $primaryColor;
        box-shadow: 0 0 0 2px rgba(0, 94, 137, 0.08);
    }
}
.notif-filter-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 7px 14px;
    border: none;
    border-radius: 6px;
    background: $primaryColor;
    color: white;
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    transition: all $transitionFast;
    font-family: inherit;
    white-space: nowrap;

    &:hover { background: $primaryDark; }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
}

/* ========== BODY ========== */
.notif-body {
    background: white;
    border: 1px solid $gray100;
    border-radius: $radiusMd;
    box-shadow: $shadowSm;
    overflow: hidden;
}

/* ---- Loading ---- */
.notif-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 48px 16px;

    span { font-size: 13px; color: $gray400; font-weight: 500; }
}
.notif-spinner {
    width: 24px;
    height: 24px;
    border: 2.5px solid $gray200;
    border-top-color: $primaryColor;
    border-radius: 50%;
    animation: notifSpin 0.7s linear infinite;
}
.notif-spin {
    width: 12px;
    height: 12px;
    border: 2px solid rgba(255,255,255,0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: notifSpin 0.7s linear infinite;
}
@keyframes notifSpin { to { transform: rotate(360deg); } }

/* ---- Empty ---- */
.notif-empty {
    text-align: center;
    padding: 48px 16px;

    h3 { font-size: 14px; font-weight: 700; color: $gray600; margin: 0 0 4px; }
    p { font-size: 12px; color: $gray400; margin: 0; }
}
.notif-empty-icon {
    width: 48px;
    height: 48px;
    border-radius: $radiusFull;
    background: $gray50;
    border: 1px solid $gray100;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 12px;

    i { font-size: 20px; color: $gray300; }
}

/* ========== NOTIFICATION CARDS ========== */
.notif-list {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    padding: 10px;
}
.notif-card {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 8px;
    border: 1px solid $gray100;
    background: white;
    animation: notifSlideIn 0.2s cubic-bezier(0.4, 0, 0.2, 1) both;
    transition: all $transitionFast;
    cursor: default;

    &:hover {
        border-color: $gray200;
        box-shadow: 0 1px 4px rgba(0,0,0,0.04);
    }
    &--unread {
        background: rgba(0, 94, 137, 0.02);
        border-left: 3px solid $primaryColor;
    }
}
@keyframes notifSlideIn {
    from { opacity: 0; transform: translateY(3px); }
    to { opacity: 1; transform: translateY(0); }
}
.notif-card-left {
    flex-shrink: 0;
    padding-top: 1px;
}
.notif-card-icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
}
/* Color variants */
.notif-clr-default {
    background: $gray100;
    color: $gray500;
    .notif-card--unread & { background: $primaryLight; color: $primaryColor; }
}
.notif-clr-success {
    background: #ecfdf5;
    color: #059669;
}
.notif-clr-info {
    background: #eff6ff;
    color: #2563eb;
}
.notif-clr-warning {
    background: #fffbeb;
    color: #d97706;
}
.notif-clr-danger {
    background: #fef2f2;
    color: #dc2626;
}
.notif-clr-purple {
    background: #f5f3ff;
    color: #7c3aed;
}
.notif-card-body {
    flex: 1;
    min-width: 0;
}
.notif-card-text {
    font-size: 13px;
    color: $gray700;
    line-height: 1.4;
    word-break: break-word;

    .notif-card--unread & {
        font-weight: 600;
        color: $gray800;
    }
}
.notif-card-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 3px;
}
.notif-card-time {
    font-size: 11px;
    color: $gray400;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 4px;

    i { font-size: 10px; }
}
.notif-card-unread-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: $primaryColor;
    flex-shrink: 0;
}

/* ========== PAGINATION BAR ========== */
.notif-pagination-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 16px;
    border-top: 1px solid $gray100;
    background: $gray50;
    gap: 12px;
    flex-wrap: wrap;
}
.notif-page-info {
    display: flex;
    align-items: center;
    gap: 14px;
}
.notif-page-info-text {
    font-size: 12px;
    color: $gray500;

    strong { color: $gray700; font-weight: 600; }
}
.notif-per-page {
    display: flex;
    align-items: center;
    gap: 6px;

    label {
        font-size: 11px;
        color: $gray400;
        font-weight: 500;
    }
}
.notif-per-page-select {
    padding: 3px 6px;
    border: 1px solid $gray200;
    border-radius: 5px;
    font-size: 12px;
    color: $gray700;
    background: white;
    outline: none;
    font-family: inherit;
    cursor: pointer;

    &:focus { border-color: $primaryColor; }
}
.notif-page-nav {
    display: flex;
    align-items: center;
    gap: 3px;
}
.notif-pg-btn {
    min-width: 30px;
    height: 30px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 6px;
    border: 1px solid $gray200;
    border-radius: 6px;
    background: white;
    color: $gray600;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all $transitionFast;
    font-family: inherit;

    &:hover:not(:disabled):not(.notif-pg-btn--active) {
        background: $gray100;
        border-color: $gray300;
        color: $gray800;
    }
    &:disabled {
        opacity: 0.35;
        cursor: not-allowed;
    }
    &--active {
        background: $primaryColor;
        border-color: $primaryColor;
        color: white;
        font-weight: 600;
    }
}
.notif-pg-ellipsis {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    font-size: 12px;
    color: $gray400;
    letter-spacing: 1px;
}

/* ========== RESPONSIVE ========== */
@media (max-width: 768px) {
    .notif-header-bar { flex-direction: column; align-items: stretch; padding: 10px 12px; }
    .notif-header-left { justify-content: flex-start; }
    .notif-header-right { flex-wrap: wrap; }
    .notif-search-wrap { flex: 1; max-width: 100%; min-width: 0; }
    .notif-date-input { flex: 1; min-width: 0; }
    .notif-list { padding: 8px; gap: 6px; grid-template-columns: repeat(2, 1fr); }
    .notif-card { padding: 8px 10px; gap: 8px; }
    .notif-card-icon { width: 28px; height: 28px; font-size: 11px; }
    .notif-pagination-bar { flex-direction: column; align-items: flex-start; padding: 10px 12px; }
    .notif-page-nav { width: 100%; justify-content: center; }
}
@media (max-width: 480px) {
    .notif-header-bar { padding: 8px 10px; border-radius: 8px; }
    .notif-body { border-radius: 8px; }
    .notif-list { padding: 6px; gap: 5px; grid-template-columns: 1fr; }
    .notif-card { padding: 7px 8px; }
    .notif-card-text { font-size: 12px; }
    .notif-filter-btn { flex: 1; justify-content: center; }
    .notif-pg-btn { min-width: 28px; height: 28px; font-size: 11px; }
}
</style>
